import React, { useState, useRef, useCallback } from 'react';
import {
    Box,
    Fab,
    Typography
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import LinkList from './LinkList';
import HelpText from './HelpText';
import CreateMeetingSidePane from './CreateMeetingSidePane';
import CreateMeetingDialog from './CreateMeetingDialog';
import useFastOpenMeeting from '../hooks/useFastOpenMeeting';
import { toast } from "sonner";
import { useTranslation } from 'react-i18next';

export default function Meetings({ accountId, appContext }) {
    const { t } = useTranslation('shared');
    const [panelOpen, setPanelOpen] = useState(false);
    // Editing or duplicating an existing meeting happens in the dialog; the
    // side pane stays the create surface.
    const [dialogLink, setDialogLink] = useState(null);
    const [editing, setEditing] = useState(false);
    const hasAutoOpened = useRef(false);

    const { openMeeting } = useFastOpenMeeting({
        onLaunchMeeting: (joinUrl) => {
            window.open(joinUrl, '_blank');
        },
        onFallbackVerify: (link) => {
            window.open(link.url, '_blank');
        },
    });

    const handleLinkCountChange = useCallback((count) => {
        if (!hasAutoOpened.current && count < 2) {
            setPanelOpen(true);
            hasAutoOpened.current = true;
        }
    }, []);

    const openEdit = useCallback((link) => {
        setDialogLink(link);
        setEditing(true);
    }, []);

    const openDuplicate = useCallback((link) => {
        setDialogLink({ ...link, title: '' });
        setEditing(false);
    }, []);

    const setFeedback = ({type, message}) => {
        if (type === "success") {
            toast.success(message);
        } else if (type === "error") {
            toast.error(message);
        } else {
            toast(message);
        }
    };

    return (
        <>
        <Box sx={{ 
            p: { xs: 2, sm: 4 }, 
            flex: 1,
            minHeight: 0,
            display: 'flex', 
            gap: 2, 
            position: 'relative' 
        }}>
            <Fab
                color="primary"
                size="medium"
                onClick={() => setPanelOpen(!panelOpen)}
                sx={{
                    position: 'absolute',
                    top: { xs: 12, sm: 28 },
                    right: { xs: 16, sm: 32 },
                    zIndex: 2,
                    transition: 'transform 0.3s ease',
                    transform: panelOpen ? 'rotate(45deg)' : 'rotate(0deg)',
                }}
            >
                <AddIcon />
            </Fab>
            <Box sx={{ flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column' }}>
                <Box sx={{ mb: 2, pr: { xs: 6, sm: 0 } }}>
                    <Typography
                        variant="h5"
                        component="h1"
                        sx={{
                            fontWeight: 500,
                            fontSize: { xs: '1.25rem', sm: '1.5rem' },
                        }}
                    >
                        {t('meetings.title')}
                    </Typography>
                </Box>

                <HelpText>
                    {t('meetings.helpText')}
                </HelpText>

                <Box sx={{ flex: 1, minHeight: 0, display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
                    <LinkList
                        onOpenMeeting={openMeeting}
                        onEditLink={openEdit}
                        onDuplicateLink={openDuplicate}
                        onLinkCountChange={handleLinkCountChange}
                        enablePagination
                        enableSearch
                    />
                </Box>
            </Box>
            <CreateMeetingSidePane
                open={panelOpen}
                onClose={() => setPanelOpen(false)}
                accountId={accountId}
                setFeedback={setFeedback}
                hideCloseButton
            />
            <CreateMeetingDialog
                open={!!dialogLink}
                onClose={() => setDialogLink(null)}
                accountId={accountId}
                setFeedback={setFeedback}
                initialLinkData={dialogLink}
                linkUuid={editing ? dialogLink?.link_uuid : undefined}
                isEditMode={editing}
            />
        </Box>
        </>
    );
}
